import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  AppBar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import './App.css';

type ScenarioKey = 'extreme' | 'two-deg';
type ChatMessage = { sender: 'user' | 'bot'; text: string };
type VariableOption = { id: number; name: string; diagramText: string };

type ClimateResponse = {
  years: number[];
  mean: number[];
  lower: number[];
  upper: number[];
};

type MonthOption = { value: number; label: string };

type GebietseinheitOption = { id: number; name: string; plz: string | null };
type KulturOption = { id: number; name: string };
type ErtragOption = { id: number; name: string };

type MassnahmeOption = {
  id: number;
  name: string;
  bild: string | null;
  mouseoverText: string | null;
};

type NebeneffektMassnahmeOption = {
  id: number;
  name: string;
};

type NebeneffektEntry = {
  nebeneffektId?: number;
  massnahmeId?: number;
  name: string;
  bewertung: number;
  info: string | null;
  link: string | null;
};

type NebeneffektDropdownOption = {
  id: number;
  name: string;
};

type MassnahmenNebeneffekteResponse = {
  massnahmen: NebeneffektMassnahmeOption[];
  nebeneffekte: NebeneffektDropdownOption[];
  selectedMassnahme: NebeneffektMassnahmeOption | null;
  selectedNebeneffekt: NebeneffektDropdownOption | null;
  mode: 'massnahme' | 'nebeneffekt';
  entries: NebeneffektEntry[];
};

type OekonomieSeries = {
  id: number;
  name: string;
  basis: number | null;
  projektion: number | null;
  years: Record<string, number>;
};

type OekonomieResponse = {
  variable: { id: number; name: string; einheit: string; diagramText: string };
  keineMassnahme: OekonomieSeries | null;
  mitMassnahme: OekonomieSeries | null;
};

type DbKomponentenKey =
  | 'ertragFrischmasse'
  | 'preis'
  | 'saatgut'
  | 'pflanzenschutz'
  | 'duengung'
  | 'variableMaschinenkosten'
  | 'hagelversicherung'
  | 'wasser';

type DbKomponenten = Record<DbKomponentenKey, number>;

type MassnahmeFallback = 'data' | 'troepfchen' | 'hitzetolerant' | 'keine';

type MassnahmeKomponentenEntry = {
  id: number;
  name: string;
  fallback: MassnahmeFallback;
  komponenten25_28: DbKomponenten;
  komponenten45_50: DbKomponenten;
};

type MassnahmeData = {
  id: number;
  name: string;
  fallback: MassnahmeFallback;
  original: DbKomponenten;
  projektion: DbKomponenten;
};

type DbKomponentenResponse = {
  komponenten: DbKomponenten;
  komponenten25_28?: DbKomponenten;
  komponenten45_50?: DbKomponenten;
  komponenten25_28Massnahme?: DbKomponenten | null;
  komponenten45_50Massnahme?: DbKomponenten | null;
  einheiten: Record<DbKomponentenKey, string>;
  jahre: number[];
  jahreProjektion?: number[];
  massnahmeId: number;
  selectedMassnahmeId?: number | null;
  selectedMassnahmeName?: string | null;
  massnahmeFallback?: MassnahmeFallback | null;
  selectedMassnahmen?: MassnahmeKomponentenEntry[];
};

const DB_KOMPONENTEN_FIELDS: ReadonlyArray<{
  key: DbKomponentenKey;
  label: string;
  defaultEinheit: string;
}> = [
  { key: 'ertragFrischmasse', label: 'Ertrag Frischmasse', defaultEinheit: 'dt/ha' },
  { key: 'preis', label: 'Preis', defaultEinheit: '€/dt' },
  { key: 'saatgut', label: 'Saatgut', defaultEinheit: '€/ha' },
  { key: 'pflanzenschutz', label: 'Pflanzenschutz', defaultEinheit: '€/ha' },
  { key: 'duengung', label: 'Düngung', defaultEinheit: '€/ha' },
  { key: 'variableMaschinenkosten', label: 'Variable Maschinenkosten', defaultEinheit: '€/ha' },
  { key: 'hagelversicherung', label: 'Hagelversicherung', defaultEinheit: '€/ha' },
  { key: 'wasser', label: 'Wasser', defaultEinheit: '€/ha' },
];

const DEFAULT_DB_KOMPONENTEN: DbKomponenten = {
  ertragFrischmasse: 0,
  preis: 0,
  saatgut: 0,
  pflanzenschutz: 0,
  duengung: 0,
  variableMaschinenkosten: 0,
  hagelversicherung: 0,
  wasser: 0,
};

function formatGermanDecimal(value: number): string {
  if (!Number.isFinite(value)) return '';
  return value.toLocaleString('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function parseGermanDecimal(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '');
  if (trimmed === '') return 0;
  const normalized = trimmed.replace(/\./g, '').replace(',', '.');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : null;
}

function isValidGermanDecimalInput(raw: string): boolean {
  return raw === '' || /^-?\d*(,\d*)?$/.test(raw.trim());
}

function normalizePastedDecimalInput(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.includes('.') && !trimmed.includes(',')) {
    return trimmed.replace('.', ',');
  }
  return trimmed;
}

type MapGeoJsonFeature = {
  type: 'Feature';
  properties?: Record<string, unknown>;
  geometry: {
    type: string;
    coordinates: unknown;
  };
};

type MapGeoJsonFeatureCollection = {
  type: 'FeatureCollection';
  features: MapGeoJsonFeature[];
};

type LandkreisMapResponse = {
  yearRange: YearRangeOption;
  variableId: number;
  geoJson: MapGeoJsonFeatureCollection;
  werte: Array<{ name: string; value: number | null }>;
};

type LandkreisTimeSeries = {
  id: number;
  name: string;
  years: Record<string, number>;
};

type LandkreisOekonomieSection = {
  einheit: string;
  keineMassnahme: LandkreisTimeSeries | null;
  mitMassnahme: LandkreisTimeSeries | null;
};

type LandkreisOekonomieResponse = {
  landkreis: { id: number; name: string };
  ertrag: LandkreisOekonomieSection;
  deckungsbeitrag: LandkreisOekonomieSection;
};

type ImpactComparisonSnapshot = {
  geoJson: MapGeoJsonFeatureCollection;
  werte: Array<{ name: string; value: number | null }>;
  kennzahlName: string;
  filterLabels: {
    kultur: string;
    ertrag: string;
    kennzahl: string;
    jahre: string;
    szenario: string;
    massnahme: string;
  };
};

type ViewName = 'climate' | 'massnahmen' | 'oekonomische-auswirkungen' | 'zusaetzliche-effekte';
type MassnahmenTab = 'deckungsbeitrag' | 'ertrag';
type YearRangeOption = 'aktuell' | '2030-2039' | '2040-2049';
type ImpactScenarioId = 1 | 2;

type InitialFilterData = {
  gebietseinheiten: GebietseinheitOption[];
  kulturen: KulturOption[];
  ertraege: ErtragOption[];
};

const scenarioOptions: { key: ScenarioKey; label: string; tooltip: string }[] = [
  {
    key: 'two-deg',
    label: '2-Grad Szenario',
    tooltip: 'SSP1-2.6 (aktiver internationaler Klimaschutz gemäß Pariser Klimaabkommen)',
  },
  {
    key: 'extreme',
    label: 'Extremes Szenario',
    tooltip: 'SSP3-7.0 (hoher Rohstoff- und Energiebedarf aus fossilen Energieträgern)',
  },
];

const impactScenarioOptions: Array<{ id: ImpactScenarioId; label: string }> = [
  { id: 1, label: 'Normales Szenario' },
  { id: 2, label: 'Extremes Szenario' },
];

const monthOptions: MonthOption[] = [
  { value: 0, label: 'Ganzes Jahr' },
  { value: 1, label: 'Januar' },
  { value: 2, label: 'Februar' },
  { value: 3, label: 'März' },
  { value: 4, label: 'April' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juni' },
  { value: 7, label: 'Juli' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'Oktober' },
  { value: 11, label: 'November' },
  { value: 12, label: 'Dezember' },
];



const placeholderReplies = [
  'Verstanden. Ich prüfe die Risiken für Ihre Auswahl.',
  'Hinweis: Die Auswirkungen variieren je nach Region.',
  'Denken Sie daran, auch Bodenfeuchte und Sortenwahl zu berücksichtigen.',
  'Für ein genaueres Bild sollten Sie die Szenarien vergleichen.',
];

/** CRA: setze REACT_APP_API_BASE_URL beim Build (z. B. Docker --build-arg). Leer = Fallback für lokale Entwicklung. */
const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8082').replace(
  /\/+$/,
  '',
);
let initialFilterDataCache: InitialFilterData | null = null;
let initialFilterDataPromise: Promise<InitialFilterData> | null = null;

const scenarioButtonPalette: Record<
  ScenarioKey,
  { main: string; hover: string; border: string }
> = {
  'two-deg': { main: '#ff9800', hover: '#fb8c00', border: '#ffb74d' },
  extreme: { main: '#d32f2f', hover: '#c62828', border: '#ef5350' },
};

const getScenarioButtonStyles = (key: ScenarioKey, isActive: boolean) => {
  const palette = scenarioButtonPalette[key];

  return {
    borderColor: palette.main,
    color: isActive ? '#fff' : palette.hover,
    backgroundColor: isActive ? palette.main : 'transparent',
    '&:hover': {
      borderColor: palette.hover,
      backgroundColor: isActive ? palette.hover : palette.border,
      color: '#fff',
    },
    '&.MuiButton-contained': {
      color: '#fff',
      backgroundColor: palette.main,
    },
    '&.MuiButton-contained:hover': {
      backgroundColor: palette.hover,
    },
    '&.MuiButton-outlined': {
      borderColor: palette.main,
    },
    '&.MuiButton-outlined:hover': {
      borderColor: palette.hover,
    },
  };
};

const getGebietseinheitOptionKey = (option: GebietseinheitOption) => `${String(option.id)}::${option.plz ?? ''}`;

const gebietseinheitFilter = (option: GebietseinheitOption, inputValue: string) => {
  const search = inputValue.trim().toLowerCase();
  if (!search) return true;
  const plz = (option.plz ?? '').toLowerCase();
  const name = option.name.toLowerCase();
  return (
    plz.includes(search) ||
    name.includes(search) ||
    `${plz} ${name}`.includes(search)
  );
};

const getImpactMapUnits = (variableName: string): { legend: string; tooltip: string } => {
  const normalized = variableName.toLowerCase();
  if (normalized.includes('deckungsbeitrag')) {
    return { legend: '€/ha', tooltip: '€' };
  }
  if (normalized.includes('ertrag')) {
    return { legend: 'dt/ha', tooltip: 'dt/ha' };
  }
  return { legend: '', tooltip: '' };
};

const buildLandkreisChartOptions = ({
  geoJson,
  werte,
  variableName,
  mapName,
}: {
  geoJson: MapGeoJsonFeatureCollection | null;
  werte: Array<{ name: string; value: number | null }>;
  variableName: string;
  mapName: string;
}) => {
  if (!geoJson || geoJson.features.length === 0) return {};

  echarts.registerMap(mapName, geoJson as any);
  const definedValues = werte
    .map((entry) => entry.value)
    .filter((value): value is number => value !== null);
  const minValue = definedValues.length > 0 ? Math.min(...definedValues) : 0;
  const maxValue = definedValues.length > 0 ? Math.max(...definedValues) : 1;
  const { legend: legendUnit, tooltip: tooltipUnit } = getImpactMapUnits(variableName);
  const formatLegendValue = (value: number) =>
    legendUnit ? `${value.toFixed(0)} ${legendUnit}` : value.toFixed(0);

  return {
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        const value = params?.data?.value;
        const valueText =
          value === null || value === undefined
            ? 'kein Wert'
            : tooltipUnit
              ? `${value.toFixed(2)} ${tooltipUnit}`
              : `${value.toFixed(2)}`;
        return `<b>${params.name}</b><br/>Mittelwert (${variableName}): ${valueText}`;
      },
    },
    visualMap: {
      min: minValue,
      max: maxValue,
      left: 16,
      bottom: 16,
      calculable: true,
      text: ['hoch', 'niedrig'],
      formatter: formatLegendValue,
      inRange: {
        color: ['#e8f5e9', '#a5d6a7', '#66bb6a', '#2e7d32'],
      },
    },
    series: [
      {
        name: variableName,
        type: 'map',
        map: mapName,
        roam: true,
        emphasis: {
          label: { show: false },
          itemStyle: { areaColor: '#ffcc80' },
        },
        itemStyle: {
          borderColor: '#37474f',
          borderWidth: 0.9,
        },
        data: werte,
      },
    ],
  };
};

function StartDisclaimer() {
  return (
    <Box className="start-disclaimer-wrap">
      <Typography variant="body2" component="p" className="start-disclaimer">
        Die Software ist derzeit nur zu Testzwecken. Daten sind nur teilweise real.
        Bei Fragen wenden Sie sich bitte an:
        <br />
        <a
          className="start-disclaimer-link"
          href="https://www.hswt.de/bernhard-schauberger"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.hswt.de/bernhard-schauberger
        </a>
      </Typography>
    </Box>
  );
}

function App() {
  const [scenario, setScenario] = useState<ScenarioKey>('two-deg');
  const [variableOptions, setVariableOptions] = useState<VariableOption[]>([]);
  const [metric, setMetric] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('0');
  const [data, setData] = useState<ClimateResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [chatCollapsed, setChatCollapsed] = useState<boolean>(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState<string>('');
  const [replyIndex, setReplyIndex] = useState<number>(0);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const [chatPos, setChatPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [showStartScreen, setShowStartScreen] = useState(true);

  // --- Filter State (moved up for dependency ordering) ---
  const [plz, setPlz] = useState<string>('');
  const [selectedGebietseinheitKey, setSelectedGebietseinheitKey] = useState<string>('');
  const [kultur, setKultur] = useState<string>('');
  const [ertrag, setErtrag] = useState<string>('');
  const allSelected = Boolean(plz && kultur && ertrag);

  const [gebietseinheiten, setGebietseinheiten] = useState<GebietseinheitOption[]>([]);
  const [kulturOptions, setKulturOptions] = useState<KulturOption[]>([]);
  const [ertragOptions, setErtragOptions] = useState<ErtragOption[]>([]);

  // --- Maßnahmen-View State -----------------------------------------------
  const [currentView, setCurrentView] = useState<ViewName>('climate');
  const [massnahmenList, setMassnahmenList] = useState<MassnahmeOption[]>([]);
  const [massnahmenDropdownList, setMassnahmenDropdownList] = useState<MassnahmeOption[]>([]);
  const [selectedMassnahmen, setSelectedMassnahmen] = useState<number[]>([]);
  const [massnahmenTab, setMassnahmenTab] = useState<MassnahmenTab>('deckungsbeitrag');
  const [impactVariable, setImpactVariable] = useState<string>('');
  const [impactYearRange, setImpactYearRange] = useState<YearRangeOption>('2030-2039');
  const [impactScenarioId, setImpactScenarioId] = useState<ImpactScenarioId>(1);
  const [impactMassnahme, setImpactMassnahme] = useState<string>('');
  const [landkreisGeoJson, setLandkreisGeoJson] = useState<MapGeoJsonFeatureCollection | null>(null);
  const [landkreisValues, setLandkreisValues] = useState<Array<{ name: string; value: number | null }>>([]);
  const [landkreisLoading, setLandkreisLoading] = useState(false);
  const [landkreisError, setLandkreisError] = useState<string | null>(null);
  const [landkreisModalOpen, setLandkreisModalOpen] = useState(false);
  const [landkreisModalLoading, setLandkreisModalLoading] = useState(false);
  const [landkreisModalError, setLandkreisModalError] = useState<string | null>(null);
  const [landkreisModalData, setLandkreisModalData] = useState<LandkreisOekonomieResponse | null>(null);
  const [selectedLandkreisName, setSelectedLandkreisName] = useState<string>('');
  const [impactComparisonSnapshot, setImpactComparisonSnapshot] =
    useState<ImpactComparisonSnapshot | null>(null);
  const [nebeneffektMassnahmen, setNebeneffektMassnahmen] = useState<NebeneffektMassnahmeOption[]>([]);
  const [selectedNebeneffektMassnahme, setSelectedNebeneffektMassnahme] = useState<string>('');
  const [nebeneffekteDropdownList, setNebeneffekteDropdownList] = useState<NebeneffektDropdownOption[]>([]);
  const [selectedNebeneffektDropdown, setSelectedNebeneffektDropdown] = useState<string>('');
  const [nebeneffektPrimaryCategory, setNebeneffektPrimaryCategory] = useState<'massnahme' | 'nebeneffekt'>('massnahme');
  const [nebeneffektDataMode, setNebeneffektDataMode] = useState<'massnahme' | 'nebeneffekt'>('massnahme');
  const [nebeneffektEntries, setNebeneffektEntries] = useState<NebeneffektEntry[]>([]);
  const [nebeneffektLoading, setNebeneffektLoading] = useState(false);
  const [nebeneffektError, setNebeneffektError] = useState<string | null>(null);
  const [hoveredOuterNode, setHoveredOuterNode] = useState<{
    name: string;
    bewertung: number;
    info: string | null;
    link: string | null;
  } | null>(null);
  const hoveredOuterNodeRef = useRef(hoveredOuterNode);
  const [oekonomieData, setOekonomieData] = useState<OekonomieResponse | null>(null);
  const [oekonomieLoading, setOekonomieLoading] = useState(false);
  const [dbInputs, setDbInputs] = useState<DbKomponenten>({ ...DEFAULT_DB_KOMPONENTEN });
  const [dbInputsOriginal, setDbInputsOriginal] = useState<DbKomponenten>({ ...DEFAULT_DB_KOMPONENTEN });
  const [dbInputsProjektion, setDbInputsProjektion] = useState<DbKomponenten>({ ...DEFAULT_DB_KOMPONENTEN });
  const [massnahmenData, setMassnahmenData] = useState<MassnahmeData[]>([]);
  const [dbEinheiten, setDbEinheiten] = useState<Record<DbKomponentenKey, string>>(() =>
    DB_KOMPONENTEN_FIELDS.reduce((acc, f) => {
      acc[f.key] = f.defaultEinheit;
      return acc;
    }, {} as Record<DbKomponentenKey, string>)
  );
  const [dbKomponentenLoading, setDbKomponentenLoading] = useState(false);
  const [dbInputFocusedKey, setDbInputFocusedKey] = useState<DbKomponentenKey | null>(null);
  const [dbInputEditText, setDbInputEditText] = useState('');
  const [carouselStart, setCarouselStart] = useState(0);
  const CAROUSEL_PAGE_SIZE = isMobile ? 2 : 4;

  useEffect(() => {
    if (!plz) {
      setVariableOptions([]);
      setMetric('');
      setData(null);
      setError(null);
      return;
    }

    const fetchVariables = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/variablen`);
        if (!response.ok) throw new Error('Fehler beim Laden der Variablen');
        const json: VariableOption[] = await response.json();
        setVariableOptions(json);
        if (json.length > 0) {
          setMetric((currentMetric) => {
            const currentExists = json.some((option) => String(option.id) === currentMetric);
            return currentExists ? currentMetric : String(json[0].id);
          });
        } else {
          setMetric('');
        }
      } catch (err) {
        console.error(err);
      }
    };

    fetchVariables();
  }, [plz]);

  const selectedVariable = variableOptions.find((v) => String(v.id) === metric);
  const [impactVariableOptions, setImpactVariableOptions] = useState<VariableOption[]>([]);

  useEffect(() => {
    if (currentView !== 'oekonomische-auswirkungen') return;
    if (impactVariableOptions.length > 0) return;

    let active = true;
    const fetchImpactVariables = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/variablen?quelle=oekonomie`);
        if (!response.ok) return;
        const json: VariableOption[] = await response.json();
        const mapSupported = json.filter((v) => v.id === 3 || v.id === 4);
        if (active) setImpactVariableOptions(mapSupported);
      } catch {
        /* ignore */
      }
    };

    fetchImpactVariables();
    return () => {
      active = false;
    };
  }, [currentView, impactVariableOptions.length]);

  useEffect(() => {
    if (impactVariableOptions.length === 0) {
      if (impactVariable !== '') {
        setImpactVariable('');
      }
      return;
    }

    const exists = impactVariableOptions.some((option) => String(option.id) === impactVariable);
    if (!exists) {
      const defaultErtrag = impactVariableOptions.find((option) =>
        option.name.toLowerCase().includes('ertrag')
      );
      setImpactVariable(String((defaultErtrag ?? impactVariableOptions[0]).id));
    }
  }, [impactVariableOptions, impactVariable]);

  const fetchData = async (
    scenarioKey: ScenarioKey,
    metricKey: string,
    gebietseinheitId: string,
    month: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        scenario: scenarioKey,
        metric: metricKey,
        month,
      });
      if (gebietseinheitId) {
        params.set('gebietseinheit', gebietseinheitId);
      }
      const response = await fetch(
        `${apiBaseUrl}/api/climate?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(`Fehler beim Laden der Daten (${response.status})`);
      }
      const json: ClimateResponse = await response.json();
      setData(json);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!plz || !metric) {
      return;
    }
    fetchData(scenario, metric, plz, selectedMonth);
  }, [scenario, metric, plz, selectedMonth]);

  // --- Maßnahmen laden wenn View aktiv ist --------------------------------
  const fetchMassnahmen = useCallback(async () => {
    if (!plz || !kultur || !ertrag) return;
    try {
      const params = new URLSearchParams({
        kultur,
        ertrag,
        gebietseinheit: plz,
        szenario: scenario,
      });
      const res = await fetch(`${apiBaseUrl}/api/massnahmen?${params}`);
      if (!res.ok) return;
      const json: MassnahmeOption[] = await res.json();
      setMassnahmenList(json);
    } catch {
      /* ignore */
    }
  }, [plz, kultur, ertrag, scenario]);

  const fetchAllMassnahmen = useCallback(async () => {
    try {
      const res = await fetch(`${apiBaseUrl}/api/massnahmen?all=1`);
      if (!res.ok) return;
      const json: MassnahmeOption[] = await res.json();
      setMassnahmenDropdownList(json);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (currentView === 'massnahmen') {
      fetchMassnahmen();
    }
  }, [currentView, fetchMassnahmen]);

  useEffect(() => {
    if (currentView === 'oekonomische-auswirkungen') {
      fetchAllMassnahmen();
    }
  }, [currentView, fetchAllMassnahmen]);

  const fetchMassnahmenNebeneffekte = useCallback(async () => {
    if (currentView !== 'zusaetzliche-effekte') return;

    setNebeneffektLoading(true);
    setNebeneffektError(null);
    setNebeneffektEntries([]);

    try {
      const params = new URLSearchParams();
      if (selectedNebeneffektDropdown) {
        params.set('nebeneffekt', selectedNebeneffektDropdown);
      } else if (selectedNebeneffektMassnahme) {
        params.set('massnahme', selectedNebeneffektMassnahme);
      }
      const query = params.toString();
      const url = `${apiBaseUrl}/api/massnahmen-nebeneffekte${query ? `?${query}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Fehler beim Laden der Nebeneffekte (${res.status})`);
      }

      const json: MassnahmenNebeneffekteResponse = await res.json();
      setNebeneffektMassnahmen(json.massnahmen ?? []);
      setNebeneffekteDropdownList(json.nebeneffekte ?? []);
      setNebeneffektDataMode(json.mode ?? 'massnahme');
      setNebeneffektEntries(json.entries ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setNebeneffektError(message);
      setNebeneffektEntries([]);
    } finally {
      setNebeneffektLoading(false);
    }
  }, [currentView, selectedNebeneffektMassnahme, selectedNebeneffektDropdown]);

  useEffect(() => {
    if (currentView === 'zusaetzliche-effekte') {
      fetchMassnahmenNebeneffekte();
    }
  }, [currentView, fetchMassnahmenNebeneffekte]);

  useEffect(() => {
    if (currentView !== 'zusaetzliche-effekte') return;
    if (selectedNebeneffektDropdown) return;
    if (nebeneffektMassnahmen.length === 0) return;

    const exists = nebeneffektMassnahmen.some((m) => String(m.id) === selectedNebeneffektMassnahme);
    if (!selectedNebeneffektMassnahme || !exists) {
      setSelectedNebeneffektMassnahme(String(nebeneffektMassnahmen[0].id));
    }
  }, [currentView, nebeneffektMassnahmen, selectedNebeneffektMassnahme, selectedNebeneffektDropdown]);

  useEffect(() => {
    if (currentView !== 'oekonomische-auswirkungen') return;
    if (massnahmenDropdownList.length === 0) {
      if (impactMassnahme !== '') {
        setImpactMassnahme('');
      }
      return;
    }
    const exists = massnahmenDropdownList.some((m) => String(m.id) === impactMassnahme);
    if (!exists) {
      const defaultKeineMassnahme = massnahmenDropdownList.find((m) =>
        m.name.toLowerCase().includes('keine')
      );
      setImpactMassnahme(String((defaultKeineMassnahme ?? massnahmenDropdownList[0]).id));
    }
  }, [currentView, massnahmenDropdownList, impactMassnahme]);

  useEffect(() => {
    if (currentView !== 'oekonomische-auswirkungen') return;
    if (!impactVariable) return;
    if (!impactMassnahme) return;

    const fetchLandkreisMap = async () => {
      setLandkreisLoading(true);
      setLandkreisError(null);

      try {
        const params = new URLSearchParams({
          variable: impactVariable,
          yearRange: impactYearRange,
          szenarioId: String(impactScenarioId),
          kultur,
          ertrag,
          massnahme: impactMassnahme,
        });

        const res = await fetch(`${apiBaseUrl}/api/landkreis-map?${params.toString()}`);
        if (!res.ok) {
          throw new Error(`Fehler beim Laden der Landkreisdaten (${res.status})`);
        }

        const json: LandkreisMapResponse = await res.json();
        setLandkreisGeoJson(json.geoJson);
        setLandkreisValues(json.werte);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
        setLandkreisError(message);
        setLandkreisGeoJson(null);
        setLandkreisValues([]);
      } finally {
        setLandkreisLoading(false);
      }
    };

    fetchLandkreisMap();
  }, [currentView, impactVariable, impactYearRange, impactScenarioId, impactMassnahme, kultur, ertrag]);

  // --- Oekonomie-Daten laden ----------------------------------------------
  const fetchOekonomie = useCallback(async () => {
    if (!plz || !kultur || !ertrag) return;
    setOekonomieLoading(true);
    try {
      const params = new URLSearchParams({
        kultur,
        ertrag,
        gebietseinheit: plz,
        szenario: scenario,
        tab: massnahmenTab,
      });
      // /api/oekonomie unterstuetzt nur EINE Massnahme im Ertrag-Tab.
      // Wir nehmen die erste der ggf. mehreren selektierten Massnahmen.
      if (selectedMassnahmen.length > 0) {
        params.set('massnahme', String(selectedMassnahmen[0]));
      }
      const res = await fetch(`${apiBaseUrl}/api/oekonomie?${params}`);
      if (!res.ok) throw new Error();
      const json: OekonomieResponse = await res.json();
      setOekonomieData(json);
    } catch {
      setOekonomieData(null);
    } finally {
      setOekonomieLoading(false);
    }
  }, [plz, kultur, ertrag, scenario, massnahmenTab, selectedMassnahmen]);

  useEffect(() => {
    if (currentView === 'massnahmen') {
      fetchOekonomie();
    }
  }, [currentView, fetchOekonomie]);

  // --- DB-Komponenten (Mittel 2025-2028 + 2045-2050) laden ----------------
  // Letzter Server-Basis-Snapshot, um zu erkennen ob sich die Basis-Daten
  // (Keine Massnahme) wirklich geaendert haben. Bei reinem Massnahmenwechsel
  // bleiben Basis und User-Eingaben in dbInputs erhalten.
  const lastBasisOriginalRef = useRef<DbKomponenten | null>(null);

  const fetchDbKomponenten = useCallback(async () => {
    if (!plz || !kultur || !ertrag) return;
    setDbKomponentenLoading(true);
    try {
      const params = new URLSearchParams({
        kultur,
        ertrag,
        gebietseinheit: plz,
        szenario: scenario,
      });
      if (selectedMassnahmen.length > 0) {
        params.set('massnahmen', selectedMassnahmen.join(','));
      }
      const res = await fetch(`${apiBaseUrl}/api/oekonomie/db-komponenten?${params}`);
      if (!res.ok) throw new Error();
      const json: DbKomponentenResponse = await res.json();
      const basis = { ...DEFAULT_DB_KOMPONENTEN, ...(json.komponenten25_28 ?? json.komponenten) };
      const projektion = { ...DEFAULT_DB_KOMPONENTEN, ...(json.komponenten45_50 ?? {}) };

      const basisKeys = Object.keys(DEFAULT_DB_KOMPONENTEN) as DbKomponentenKey[];
      const basisChanged =
        !lastBasisOriginalRef.current ||
        basisKeys.some((k) => lastBasisOriginalRef.current![k] !== basis[k]);

      if (basisChanged) {
        setDbInputs(basis);
        setDbInputsOriginal(basis);
        setDbInputsProjektion(projektion);
        lastBasisOriginalRef.current = basis;
      }

      const massnahmen: MassnahmeData[] = (json.selectedMassnahmen ?? []).map((m) => ({
        id: m.id,
        name: m.name,
        fallback: m.fallback,
        original: { ...DEFAULT_DB_KOMPONENTEN, ...m.komponenten25_28 },
        projektion: { ...DEFAULT_DB_KOMPONENTEN, ...m.komponenten45_50 },
      }));
      setMassnahmenData(massnahmen);

      if (json.einheiten) {
        setDbEinheiten((prev) => ({ ...prev, ...json.einheiten }));
      }
    } catch {
      setDbInputs({ ...DEFAULT_DB_KOMPONENTEN });
      setDbInputsOriginal({ ...DEFAULT_DB_KOMPONENTEN });
      setDbInputsProjektion({ ...DEFAULT_DB_KOMPONENTEN });
      setMassnahmenData([]);
      lastBasisOriginalRef.current = null;
    } finally {
      setDbKomponentenLoading(false);
    }
  }, [plz, kultur, ertrag, scenario, selectedMassnahmen]);

  useEffect(() => {
    if (currentView === 'massnahmen' && massnahmenTab === 'deckungsbeitrag') {
      fetchDbKomponenten();
    }
  }, [currentView, massnahmenTab, fetchDbKomponenten]);

  const chartOptions = useMemo(() => {
    if (!data) return {};

    const yMin = Math.min(...data.lower);
    const yMax = Math.max(...data.upper);
    const padding = Math.max(1, (yMax - yMin) * 0.1);

    const bandDiff = data.upper.map((value, idx) =>
      Number((value - data.lower[idx]).toFixed(2))
    );
    const axisLabelStyle = { fontSize: 13 };
    const axisNameStyle = { fontSize: 14, padding: [0, 0, 6, 0], align: 'left' };

    const formatValue = (value: number) => {
      const abs = Math.abs(value);
      // Kleine Werte praeziser anzeigen, damit z.B. ein Mittelwert von 0,17
      // gegenueber einem Band knapp darueber/darunter erkennbar bleibt.
      const digits = abs > 0 && abs < 1 ? 3 : 2;
      return Number(value).toLocaleString('de-DE', { maximumFractionDigits: digits });
    };

    return {
      tooltip: {
        trigger: 'axis',
        // Eigener Formatter: die Hilfsserien ("Band unten"/"Band oben") enthalten
        // gestapelte Roh-/Differenzwerte. Hier werden stattdessen die echten Zahlen
        // (Mittelwert sowie unteres und oberes Band) angezeigt.
        formatter: (params: any) => {
          const list = Array.isArray(params) ? params : [params];
          if (list.length === 0) return '';
          const idx = list[0].dataIndex as number;
          const year = data.years[idx];
          const meanVal = data.mean[idx];
          const lowerVal = data.lower[idx];
          const upperVal = data.upper[idx];
          const meanColor = '#1f6fb2';
          const bandColor = 'rgba(118,146,255,0.6)';
          const dot = (color: string) =>
            `<span style="display:inline-block;margin-right:6px;border-radius:50%;width:10px;height:10px;background-color:${color}"></span>`;
          return (
            `<strong>${String(year)}</strong><br/>` +
            `${dot(meanColor)}Mittelwert: <strong>${formatValue(meanVal)}</strong><br/>` +
            `${dot(bandColor)}Band: ${formatValue(lowerVal)} – ${formatValue(upperVal)}`
          );
        },
      },
      grid: {
        // gleiche Ränder links/rechts für symmetrische Einbettung
        left: isMobile ? 16 : 28,
        right: isMobile ? 16 : 28,
        top: 42,
        bottom: 60,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: data.years,
        name: 'Jahr',
        nameLocation: 'center',
        nameGap: 28,
        boundaryGap: false,
        axisLabel: axisLabelStyle,
        nameTextStyle: axisNameStyle,
      },
      yAxis: {
        type: 'value',
        name: selectedVariable?.diagramText ?? '',
        nameLocation: 'end',
        nameGap: 10,
        nameRotate: 0,
        min: Math.floor(yMin - padding),
        max: Math.ceil(yMax + padding),
        axisLabel: axisLabelStyle,
        nameTextStyle: axisNameStyle,
      },
      series: [
        {
          name: 'Band unten',
          type: 'line',
          data: data.lower,
          lineStyle: { opacity: 0 },
          stack: 'confidence',
          // 'all': auch negative Werte werden zur Basis gestapelt, damit das Band
          // bei Variablen mit negativem Unterband (z.B. Minimaltemperatur) korrekt
          // zwischen unten und oben gefuellt wird.
          stackStrategy: 'all',
          symbol: 'none',
          silent: true,
        },
        {
          name: 'Band oben',
          type: 'line',
          data: bandDiff,
          lineStyle: { opacity: 0 },
          stack: 'confidence',
          stackStrategy: 'all',
          symbol: 'none',
          silent: true,
          areaStyle: { color: 'rgba(118,146,255,0.25)' },
        },
        {
          name: 'Mittelwert',
          type: 'line',
          data: data.mean,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 3, color: '#1f6fb2' },
          itemStyle: { color: '#1f6fb2' },
        },
      ],
    };
  }, [data, metric, isMobile]);

  const selectedImpactVariableName =
    impactVariableOptions.find((v) => String(v.id) === impactVariable)?.name ?? 'Kennzahl';

  const landkreisChartOptions = useMemo(
    () =>
      buildLandkreisChartOptions({
        geoJson: landkreisGeoJson,
        werte: landkreisValues,
        variableName: selectedImpactVariableName,
        mapName: 'de-landkreise-live',
      }),
    [landkreisGeoJson, landkreisValues, selectedImpactVariableName]
  );

  const comparisonLandkreisChartOptions = useMemo(
    () =>
      buildLandkreisChartOptions({
        geoJson: impactComparisonSnapshot?.geoJson ?? null,
        werte: impactComparisonSnapshot?.werte ?? [],
        variableName: impactComparisonSnapshot?.kennzahlName ?? 'Kennzahl',
        mapName: 'de-landkreise-compare',
      }),
    [impactComparisonSnapshot]
  );

  const landkreisIdByName = useMemo(() => {
    const ids: Record<string, number> = {};
    if (!landkreisGeoJson) return ids;

    landkreisGeoJson.features.forEach((feature) => {
      const props = feature.properties ?? {};
      const name = typeof props.name === 'string' ? props.name : '';
      const rawId = props.id;
      const id = typeof rawId === 'number' ? rawId : Number(rawId);
      if (name && Number.isFinite(id) && id > 0) {
        ids[name] = id;
      }
    });

    return ids;
  }, [landkreisGeoJson]);

  const handleLandkreisMapClick = useCallback(async (params: any) => {
    const landkreisName = typeof params?.name === 'string' ? params.name : '';
    if (!landkreisName) return;

    const landkreisId = landkreisIdByName[landkreisName];
    if (!landkreisId) return;

    setSelectedLandkreisName(landkreisName);
    setLandkreisModalOpen(true);
    setLandkreisModalLoading(true);
    setLandkreisModalError(null);
    setLandkreisModalData(null);

    try {
      const query = new URLSearchParams({
        landkreisId: String(landkreisId),
        szenarioId: String(impactScenarioId),
        kultur,
        ertrag,
        massnahme: impactMassnahme,
      });
      const res = await fetch(`${apiBaseUrl}/api/landkreis-oekonomie?${query.toString()}`);
      if (!res.ok) {
        throw new Error(`Fehler beim Laden der Landkreis-Details (${res.status})`);
      }
      const json: LandkreisOekonomieResponse = await res.json();
      setLandkreisModalData(json);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setLandkreisModalError(message);
    } finally {
      setLandkreisModalLoading(false);
    }
  }, [landkreisIdByName, impactScenarioId, kultur, ertrag, impactMassnahme]);

  const landkreisChartEvents = useMemo(
    () => ({
      click: (params: any) => {
        void handleLandkreisMapClick(params);
      },
    }),
    [handleLandkreisMapClick]
  );

  const landkreisModalChartOptions = useMemo(() => {
    if (!landkreisModalData) return {};

    const ertragKeine = landkreisModalData.ertrag.keineMassnahme?.years ?? {};
    const ertragMit = landkreisModalData.ertrag.mitMassnahme?.years ?? {};
    const dbKeine = landkreisModalData.deckungsbeitrag.keineMassnahme?.years ?? {};
    const dbMit = landkreisModalData.deckungsbeitrag.mitMassnahme?.years ?? {};

    const allYears = new Set<number>();
    [ertragKeine, ertragMit, dbKeine, dbMit].forEach((series) => {
      Object.keys(series).forEach((year) => allYears.add(Number(year)));
    });
    const sortedYears = Array.from(allYears)
      .filter((year) => Number.isFinite(year))
      .sort((a, b) => a - b);

    if (sortedYears.length === 0) return {};

    const maxVisibleYearLabels = isMobile ? 5 : 8;
    const yearLabelInterval = Math.max(0, Math.ceil(sortedYears.length / maxVisibleYearLabels) - 1);
    const series: any[] = [];

    const pushSeries = (
      name: string,
      valuesByYear: Record<string, number>,
      color: string,
      yAxisIndex: number,
      dashed = false
    ) => {
      if (Object.keys(valuesByYear).length === 0) return;
      series.push({
        name,
        type: 'line',
        yAxisIndex,
        data: sortedYears.map((year) => valuesByYear[String(year)] ?? null),
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { width: 2, color, type: dashed ? 'dashed' : 'solid' },
        itemStyle: { color },
      });
    };

    pushSeries(
      `Ertrag: ${landkreisModalData.ertrag.keineMassnahme?.name ?? 'Keine Maßnahme'}`,
      ertragKeine,
      '#9e9e9e',
      0
    );
    pushSeries(
      `Ertrag: ${landkreisModalData.ertrag.mitMassnahme?.name ?? 'Mit Maßnahme'}`,
      ertragMit,
      '#ff9800',
      0
    );
    pushSeries(
      `Deckungsbeitrag: ${landkreisModalData.deckungsbeitrag.keineMassnahme?.name ?? 'Keine Maßnahme'}`,
      dbKeine,
      '#1565c0',
      1,
      true
    );
    pushSeries(
      `Deckungsbeitrag: ${landkreisModalData.deckungsbeitrag.mitMassnahme?.name ?? 'Mit Maßnahme'}`,
      dbMit,
      '#26a69a',
      1,
      true
    );

    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 32, right: 38, top: 42, bottom: 70, containLabel: true },
      xAxis: {
        type: 'category',
        data: sortedYears,
        name: 'Jahr',
        nameLocation: 'center',
        nameGap: 28,
        boundaryGap: false,
        axisLabel: {
          interval: yearLabelInterval,
          hideOverlap: true,
        },
      },
      yAxis: [
        {
          type: 'value',
          name: `Ertrag (${landkreisModalData.ertrag.einheit})`,
          nameLocation: 'end',
          nameGap: 10,
        },
        {
          type: 'value',
          name: `Deckungsbeitrag (${landkreisModalData.deckungsbeitrag.einheit})`,
          nameLocation: 'end',
          nameGap: 10,
        },
      ],
      series,
    };
  }, [landkreisModalData, isMobile]);

  const activeNebeneffektChartMode = useMemo((): 'massnahme' | 'nebeneffekt' => {
    if (nebeneffektPrimaryCategory === 'massnahme' && selectedNebeneffektMassnahme) {
      return 'massnahme';
    }
    if (nebeneffektPrimaryCategory === 'nebeneffekt' && selectedNebeneffektDropdown) {
      return 'nebeneffekt';
    }
    if (selectedNebeneffektMassnahme) return 'massnahme';
    if (selectedNebeneffektDropdown) return 'nebeneffekt';
    return nebeneffektPrimaryCategory;
  }, [nebeneffektPrimaryCategory, selectedNebeneffektMassnahme, selectedNebeneffektDropdown]);

  const nebeneffektChartOptions = useMemo(() => {
    if (nebeneffektEntries.length === 0) {
      return {};
    }

    const isMassnahmeMode = nebeneffektDataMode === 'massnahme';

    const colorForBewertung = (bewertung: number): string => {
      if (bewertung === 1) return '#2e7d32';
      if (bewertung === 2) return '#c62828';
      return '#757575';
    };

    const bewertungText = (bewertung: number): string => {
      if (bewertung === 1) return 'positiv';
      if (bewertung === 2) return 'negativ';
      return 'neutral';
    };

    let centerNodeId: string;
    let centerName: string;
    let centerLabel: string;
    let outerLabel: string;

    if (isMassnahmeMode) {
      centerNodeId = `massnahme-${selectedNebeneffektMassnahme}`;
      centerName =
        nebeneffektMassnahmen.find((option) => String(option.id) === selectedNebeneffektMassnahme)?.name ??
        'Ausgewählte Maßnahme';
      centerLabel = 'Maßnahme';
      outerLabel = 'Nebeneffekt';
    } else {
      centerNodeId = `nebeneffekt-${selectedNebeneffektDropdown}`;
      centerName =
        nebeneffekteDropdownList.find((option) => String(option.id) === selectedNebeneffektDropdown)?.name ??
        'Ausgewählter Nebeneffekt';
      centerLabel = 'Nebeneffekt';
      outerLabel = 'Maßnahme';
    }

    const bewertungOrder = (b: number) => (b === 1 ? 0 : b === 0 || b === 3 ? 1 : 2);
    const sortedEntries = [...nebeneffektEntries].sort(
      (a, b) => bewertungOrder(a.bewertung) - bewertungOrder(b.bewertung)
    );

    const nodes: any[] = [
      {
        id: centerNodeId,
        name: centerName,
        symbolSize: 76,
        itemStyle: { color: '#1565c0' },
        label: {
          show: true,
          fontWeight: 700,
          fontSize: 16,
        },
      },
    ];

    sortedEntries.forEach((entry, i) => {
      const entryId = isMassnahmeMode
        ? `nebeneffekt-${entry.nebeneffektId}`
        : `massnahme-${entry.massnahmeId}`;
      nodes.push({
        id: entryId,
        name: entry.name,
        symbolSize: 58,
        itemStyle: { color: colorForBewertung(entry.bewertung) },
        label: { show: true, fontSize: 14 },
        bewertung: entry.bewertung,
        info: entry.info,
        link: entry.link,
      });
    });

    const links = sortedEntries.map((entry) => {
      const targetId = isMassnahmeMode
        ? `nebeneffekt-${entry.nebeneffektId}`
        : `massnahme-${entry.massnahmeId}`;
      return {
        source: centerNodeId,
        target: targetId,
        lineStyle: {
          width: 5,
          color: colorForBewertung(entry.bewertung),
        },
        bewertung: entry.bewertung,
        info: entry.info,
        link: entry.link,
      };
    });

    return {
      animationDurationUpdate: 600,
      tooltip: {
        trigger: 'item',
        confine: true,
        formatter: (params: any) => {
          if (params.dataType === 'edge') {
            return `<strong>Verknüpfung</strong><br/>Bewertung: ${bewertungText(Number(params.data?.bewertung ?? 0))}`;
          }

          if (params.data?.id === centerNodeId) {
            return `<strong>${centerLabel}:</strong> ${params.name}`;
          }

          const bewertung = Number(params.data?.bewertung ?? 0);
          return `<strong>${outerLabel}:</strong> ${params.name}<br/>Bewertung: ${bewertungText(bewertung)}`;
        },
      },
      series: [
        {
          type: 'graph',
          layout: 'force',
          roam: false,
          zoom: 0.82,
          scaleLimit: { min: 0.82, max: 0.82 },
          draggable: true,
          data: nodes,
          links,
          force: {
            repulsion: 340,
            gravity: 0.08,
            edgeLength: [100, 180],
          },
          emphasis: {
            focus: 'adjacency',
            lineStyle: { width: 7 },
          },
          label: {
            position: 'right',
            distance: 12,
            fontSize: 14,
          },
        },
      ],
    };
  }, [selectedNebeneffektMassnahme, selectedNebeneffektDropdown, nebeneffektDataMode, nebeneffektEntries, nebeneffektMassnahmen, nebeneffekteDropdownList]);

  const nebeneffektCenterNodeId = useMemo(() => {
    if (nebeneffektDataMode === 'massnahme') return `massnahme-${selectedNebeneffektMassnahme}`;
    return `nebeneffekt-${selectedNebeneffektDropdown}`;
  }, [nebeneffektDataMode, selectedNebeneffektMassnahme, selectedNebeneffektDropdown]);

  const handleNebeneffektNodeHover = useCallback((params: any) => {
    if (params.dataType !== 'node') return;
    if (params.data?.id === nebeneffektCenterNodeId) return;
    const node = {
      name: params.data?.name ?? '',
      bewertung: Number(params.data?.bewertung ?? 0),
      info: params.data?.info ?? null,
      link: params.data?.link ?? null,
    };
    hoveredOuterNodeRef.current = node;
    setHoveredOuterNode(node);
  }, [nebeneffektCenterNodeId]);

  const nebeneffektChartEvents = useMemo(() => ({
    mouseover: handleNebeneffektNodeHover,
  }), [handleNebeneffektNodeHover]);

  const hasLandkreisModalSeries =
    Array.isArray((landkreisModalChartOptions as any).series) &&
    (landkreisModalChartOptions as any).series.length > 0;

  // --- Deckungsbeitrag-Basis (Ø 2025-2028) aus den Komponenten -------------
  const deckungsbeitragBasis = useMemo(() => {
    const erloes = (dbInputs.ertragFrischmasse || 0) * (dbInputs.preis || 0);
    const variableKosten =
      (dbInputs.saatgut || 0) +
      (dbInputs.pflanzenschutz || 0) +
      (dbInputs.duengung || 0) +
      (dbInputs.variableMaschinenkosten || 0) +
      (dbInputs.hagelversicherung || 0) +
      (dbInputs.wasser || 0);
    return Math.round((erloes - variableKosten) * 100) / 100;
  }, [dbInputs]);

  // --- Projektion (Ø 2045-2050): Komponenten werden mit dem Faktor
  // (Nutzereingabe / Original-Basismittel) skaliert. Bei Original=0 bleibt
  // der Projektionswert unveraendert (Faktor 1).
  const dbInputsProjektionSkaliert = useMemo<DbKomponenten>(() => {
    const out = { ...DEFAULT_DB_KOMPONENTEN };
    (Object.keys(DEFAULT_DB_KOMPONENTEN) as DbKomponentenKey[]).forEach((key) => {
      const original = dbInputsOriginal[key] || 0;
      const aktuell = dbInputs[key] || 0;
      const proj = dbInputsProjektion[key] || 0;
      const faktor = original === 0 ? 1 : aktuell / original;
      out[key] = proj * faktor;
    });
    return out;
  }, [dbInputs, dbInputsOriginal, dbInputsProjektion]);

  const deckungsbeitragProjektion = useMemo(() => {
    const p = dbInputsProjektionSkaliert;
    const erloes = (p.ertragFrischmasse || 0) * (p.preis || 0);
    const variableKosten =
      (p.saatgut || 0) +
      (p.pflanzenschutz || 0) +
      (p.duengung || 0) +
      (p.variableMaschinenkosten || 0) +
      (p.hagelversicherung || 0) +
      (p.wasser || 0);
    return Math.round((erloes - variableKosten) * 100) / 100;
  }, [dbInputsProjektionSkaliert]);

  // --- Projektion 2045-2050 fuer alle ausgewaehlten Massnahmen -------------
  // Selbe Skalierungslogik: Faktor = userInput / Basis-Original wird auf die
  // Projektionswerte jeder Massnahme angewendet.
  const massnahmenProjektionen = useMemo<Array<{ id: number; name: string; value: number }>>(() => {
    return massnahmenData.map((m) => {
      const scaled = { ...DEFAULT_DB_KOMPONENTEN };
      (Object.keys(DEFAULT_DB_KOMPONENTEN) as DbKomponentenKey[]).forEach((key) => {
        const original = dbInputsOriginal[key] || 0;
        const aktuell = dbInputs[key] || 0;
        const proj = m.projektion[key] || 0;
        const faktor = original === 0 ? 1 : aktuell / original;
        scaled[key] = proj * faktor;
      });
      const erloes = (scaled.ertragFrischmasse || 0) * (scaled.preis || 0);
      const variableKosten =
        (scaled.saatgut || 0) +
        (scaled.pflanzenschutz || 0) +
        (scaled.duengung || 0) +
        (scaled.variableMaschinenkosten || 0) +
        (scaled.hagelversicherung || 0) +
        (scaled.wasser || 0);
      return {
        id: m.id,
        name: m.name,
        value: Math.round((erloes - variableKosten) * 100) / 100,
      };
    });
  }, [dbInputs, dbInputsOriginal, massnahmenData]);

  // --- Oekonomie-Diagramm --------------------------------------------------
  const oekonomieChartOptions = useMemo(() => {
    const einheit = '€/ha';

    if (massnahmenTab === 'ertrag') {
      if (!oekonomieData) return {};
      const ertragEinheit = oekonomieData.variable?.einheit ?? 'dt/ha';
      const keine = oekonomieData.keineMassnahme;
      const mit = oekonomieData.mitMassnahme;

      // --- Liniendiagramm für Ertrag ---
      const allYears = new Set<number>();
      if (keine?.years) Object.keys(keine.years).forEach((y) => allYears.add(Number(y)));
      if (mit?.years) Object.keys(mit.years).forEach((y) => allYears.add(Number(y)));
      const sortedYears = Array.from(allYears).sort((a, b) => a - b);
      const maxVisibleYearLabels = isMobile ? 5 : 8;
      const yearLabelInterval = Math.max(0, Math.ceil(sortedYears.length / maxVisibleYearLabels) - 1);

      const series: any[] = [];
      if (keine) {
        series.push({
          name: keine.name,
          type: 'line',
          data: sortedYears.map((y) => keine.years[String(y)] ?? null),
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: { width: 2, color: '#9e9e9e' },
          itemStyle: { color: '#9e9e9e' },
        });
      }
      if (mit) {
        series.push({
          name: mit.name,
          type: 'line',
          data: sortedYears.map((y) => mit.years[String(y)] ?? null),
          symbol: 'circle',
          symbolSize: 5,
          lineStyle: { width: 2, color: '#ff9800' },
          itemStyle: { color: '#ff9800' },
        });
      }

      return {
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0 },
        grid: { left: 28, right: 28, top: 42, bottom: 60, containLabel: true },
        xAxis: {
          type: 'category',
          data: sortedYears,
          name: 'Jahr',
          nameLocation: 'center',
          nameGap: 28,
          boundaryGap: false,
          axisLabel: {
            interval: yearLabelInterval,
            hideOverlap: true,
          },
        },
        yAxis: { type: 'value', name: `Ertrag (${ertragEinheit})`, nameLocation: 'end', nameGap: 10 },
        series,
      };
    }

    // --- Balkendiagramm Deckungsbeitrag --------------------------------
    // Cluster auf der X-Achse: "Keine Massnahme" buendelt Basis + DB 45-50,
    // jede ausgewaehlte Massnahme erhaelt eine eigene Kategorie mit ihrem
    // eigenen DB-45-50-Balken.
    const massnahmenFarben = ['#4caf50', '#2196f3'];
    const categories: string[] = ['Keine Maßnahme', ...massnahmenProjektionen.map((m) => m.name)];
    const totalCats = categories.length;

    const padArray = (val: number | null, index: number): (number | null)[] => {
      const arr: (number | null)[] = new Array(totalCats).fill(null);
      arr[index] = val;
      return arr;
    };

    const series: any[] = [
      {
        name: 'DB Basis (Ø 25-28)',
        type: 'bar',
        data: padArray(deckungsbeitragBasis, 0),
        itemStyle: { color: '#bdbdbd' },
        barMaxWidth: 80,
      },
      {
        name: 'DB 45-50',
        type: 'bar',
        data: padArray(deckungsbeitragProjektion, 0),
        itemStyle: { color: '#ff9800' },
        barMaxWidth: 80,
      },
    ];
    massnahmenProjektionen.forEach((m, idx) => {
      series.push({
        name: `DB 45-50 ${m.name}`,
        type: 'bar',
        data: padArray(m.value, idx + 1),
        itemStyle: { color: massnahmenFarben[idx % massnahmenFarben.length] },
        barMaxWidth: 80,
      });
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: any[]) => {
          let html = `<b>${params[0]?.name ?? ''}</b><br/>`;
          params.forEach((p: any) => {
            if (p.value === null || p.value === undefined) return;
            const val = Number(p.value);
            html += `${p.marker} ${p.seriesName}: ${val.toFixed(0)} ${einheit}<br/>`;
          });
          return html;
        },
      },
      legend: { bottom: 0 },
      grid: { left: 28, right: 28, top: 42, bottom: 60, containLabel: true },
      xAxis: { type: 'category', data: categories, axisLabel: { interval: 0, rotate: 0 } },
      yAxis: {
        type: 'value',
        name: `Deckungsbeitrag (${einheit})`,
        nameLocation: 'end',
        nameGap: 10,
        nameRotate: 0,
        nameTextStyle: { align: 'left' },
      },
      series,
    };
  }, [
    oekonomieData,
    massnahmenTab,
    deckungsbeitragBasis,
    deckungsbeitragProjektion,
    massnahmenProjektionen,
    isMobile,
  ]);

  const handleMetricChange = (event: SelectChangeEvent) => {
    setMetric(event.target.value);
  };

  const handleMonthChange = (event: SelectChangeEvent) => {
    setSelectedMonth(event.target.value);
  };

  const selectMetricByName = (targetName: string) => {
    const match = variableOptions.find(
      (option) => option.name.trim().toLowerCase() === targetName.trim().toLowerCase()
    );
    if (match) {
      setMetric(String(match.id));
    }
  };

  const handleSendMessage = () => {
    const trimmed = chatInput.trim();
    if (!trimmed) return;
    const nextReply = placeholderReplies[replyIndex % placeholderReplies.length];
    setChatMessages((prev) => [...prev, { sender: 'user', text: trimmed }]);
    setChatInput('');
    setTimeout(() => {
      setChatMessages((prev) => [...prev, { sender: 'bot', text: nextReply }]);
      setReplyIndex((idx) => idx + 1);
    }, 300);
  };

  const chatHasBotResponse = chatMessages.some((m) => m.sender === 'bot');

  useEffect(() => {
    let isActive = true;

    const applyInitialData = (data: InitialFilterData) => {
      if (!isActive) return;
      setGebietseinheiten(data.gebietseinheiten);
      setKulturOptions(data.kulturen);
      setErtragOptions(data.ertraege);
    };

    if (initialFilterDataCache) {
      applyInitialData(initialFilterDataCache);
      return () => {
        isActive = false;
      };
    }

    if (!initialFilterDataPromise) {
      initialFilterDataPromise = Promise.all([
        fetch(`${apiBaseUrl}/api/gebietseinheiten`).then((r) => r.json() as Promise<GebietseinheitOption[]>),
        fetch(`${apiBaseUrl}/api/kulturen`).then((r) => r.json() as Promise<KulturOption[]>),
        fetch(`${apiBaseUrl}/api/ertraege`).then((r) => r.json() as Promise<ErtragOption[]>),
      ]).then(([gebietseinheiten, kulturen, ertraege]) => {
        const payload = { gebietseinheiten, kulturen, ertraege };
        initialFilterDataCache = payload;
        return payload;
      });
    }

    initialFilterDataPromise
      .then((data) => applyInitialData(data))
      .catch(() => {});

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (isMobile) return;
    const el = chatRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const left = Math.max(8, (window.innerWidth - rect.width) / 2);
    const top = Math.max(8, window.innerHeight - rect.height - 16);
    setChatPos({ top, left });
  }, [chatCollapsed, chatHasBotResponse, allSelected, isMobile]);

  useEffect(() => {
    if (isMobile) return;
    const el = chatRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);
    setChatPos((prev) => {
      const maxLeft = Math.max(0, window.innerWidth - rect.width - 8);
      const maxTop = Math.max(8, window.innerHeight - rect.height - 12);
      const nextLeft = clamp(prev.left, 0, maxLeft);
      const nextTop = clamp(prev.top, 8, maxTop);
      if (nextLeft === prev.left && nextTop === prev.top) return prev;
      return { left: nextLeft, top: nextTop };
    });
  }, [chatMessages, chatCollapsed, chatHasBotResponse, isMobile]);

  useEffect(() => {
    if (isMobile || !dragging) return;

    const handleMove = (e: MouseEvent) => {
      const el = chatRef.current;
      const width = el?.offsetWidth ?? 320;
      const height = el?.offsetHeight ?? 220;
      const newLeft = e.clientX - dragOffset.x;
      const newTop = e.clientY - dragOffset.y;
      const clampedLeft = Math.min(Math.max(0, newLeft), window.innerWidth - width);
      const clampedTop = Math.min(Math.max(0, newTop), window.innerHeight - height);
      setChatPos({ left: clampedLeft, top: clampedTop });
    };

    const handleUp = () => setDragging(false);

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, dragOffset, isMobile]);

  const startDrag = (e: React.MouseEvent) => {
    if (isMobile) return;
    const el = chatRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setDragging(true);
  };

  const headerSpacerHeight = useMemo(() => (isMobile ? '72px' : '84px'), [isMobile]);
  const selectedGebietseinheit = useMemo(
    () =>
      gebietseinheiten.find((g) => getGebietseinheitOptionKey(g) === selectedGebietseinheitKey) ??
      gebietseinheiten.find((g) => String(g.id) === plz) ??
      null,
    [gebietseinheiten, plz, selectedGebietseinheitKey]
  );

  const renderGebietseinheitAutocomplete = ({
    size,
    className,
    fullWidth = false,
    popperZIndex,
  }: {
    size: 'small' | 'medium';
    className: string;
    fullWidth?: boolean;
    popperZIndex?: number;
  }) => (
    <Autocomplete
      options={gebietseinheiten}
      value={selectedGebietseinheit}
      onChange={(_, newValue) => {
        setPlz(newValue ? String(newValue.id) : '');
        setSelectedGebietseinheitKey(newValue ? getGebietseinheitOptionKey(newValue) : '');
      }}
      isOptionEqualToValue={(option, value) =>
        getGebietseinheitOptionKey(option) === getGebietseinheitOptionKey(value)
      }
      getOptionLabel={(option) => (option.plz ? `${option.plz} – ${option.name}` : option.name)}
      filterOptions={(options, state) =>
        options.filter((option) => gebietseinheitFilter(option, state.inputValue))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label="Gebietseinheit"
          InputLabelProps={{ shrink: true }}
        />
      )}
      className={className}
      fullWidth={fullWidth}
      clearOnEscape
      slotProps={
        popperZIndex
          ? {
              popper: {
                sx: { zIndex: popperZIndex },
              },
            }
          : undefined
      }
    />
  );

  useEffect(() => {
    if (!allSelected) {
      setShowStartScreen(true);
      return;
    }
    const timer = setTimeout(() => setShowStartScreen(false), 700);
    return () => clearTimeout(timer);
  }, [allSelected]);

  const chatClassNames = [
    'chat-widget',
    chatHasBotResponse ? 'expanded' : 'compact',
    chatCollapsed ? 'collapsed' : '',
    isMobile ? 'mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hasImpactComparison = impactComparisonSnapshot !== null;

  const handleToggleImpactComparison = () => {
    if (hasImpactComparison) {
      setImpactComparisonSnapshot(null);
      return;
    }

    if (!landkreisGeoJson || landkreisGeoJson.features.length === 0) return;

    const kulturName = kulturOptions.find((option) => String(option.id) === kultur)?.name ?? 'Nicht gewählt';
    const ertragName = ertragOptions.find((option) => String(option.id) === ertrag)?.name ?? 'Nicht gewählt';
    const szenarioName =
      impactScenarioOptions.find((option) => option.id === impactScenarioId)?.label ?? 'Unbekannt';
    const massnahmeName =
      massnahmenDropdownList.find((option) => String(option.id) === impactMassnahme)?.name ??
      'Nicht gewählt';

    setImpactComparisonSnapshot({
      geoJson: landkreisGeoJson,
      werte: landkreisValues.map((entry) => ({ ...entry })),
      kennzahlName: selectedImpactVariableName,
      filterLabels: {
        kultur: kulturName,
        ertrag: ertragName,
        kennzahl: selectedImpactVariableName,
        jahre: impactYearRange,
        szenario: szenarioName,
        massnahme: massnahmeName,
      },
    });
  };

  return (
    <Box className={`app ${!allSelected ? 'start-screen' : ''}`}>
      <AppBar position="fixed" color="success" className="appbar">
        <Toolbar className="toolbar">
          <Box className="toolbar-left">
            {isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="Filter öffnen"
                onClick={() => setMobileFiltersOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            )}
            {allSelected && (
              <Typography variant="h6" className="brand">
                Klima Risiko Manager
              </Typography>
            )}
          </Box>

          {!isMobile && allSelected && (
            <Stack
              direction="row"
              spacing={2}
              className="top-select-stack"
              alignItems="flex-start"
            >
              {renderGebietseinheitAutocomplete({
                size: 'small',
                className: 'top-select',
              })}
              <TextField
                select
                size="small"
                label="Kultur"
                value={kultur}
                onChange={(e) => setKultur(e.target.value)}
                className="top-select"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>Bitte wählen</em>
                </MenuItem>
                {kulturOptions.map((k) => (
                  <MenuItem key={k.id} value={String(k.id)}>
                    {k.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Ertrag"
                value={ertrag}
                onChange={(e) => setErtrag(e.target.value)}
                className="top-select"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">
                  <em>Bitte wählen</em>
                </MenuItem>
                {ertragOptions.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      <Box className="header-spacer" sx={{ height: headerSpacerHeight }} />

      {/* Desktop Start Screen - gleiche Darstellung wie mobil */}
      {!isMobile && showStartScreen && (
        <Box className={`desktop-start-screen start-overlay ${allSelected ? 'fade-out' : ''}`}>
          <Box className="desktop-start-inner">
            <Typography variant="h4" className="mobile-start-title">
              Willkommen beim KaRisMa Klima-Risiko-Manager
            </Typography>
            <Typography variant="body1" className="mobile-start-subtitle">
              Bitte geben Sie die folgenden Daten ein bzw. treffen Sie eine Auswahl, um Ergebnisse angezeigt zu bekommen.
            </Typography>
            <StartDisclaimer />
            <Stack spacing={2} className="desktop-start-selects">
              {renderGebietseinheitAutocomplete({
                size: 'medium',
                className: 'mobile-start-select',
                fullWidth: true,
                popperZIndex: 1500,
              })}
              <TextField
                select
                size="medium"
                label="Kultur"
                value={kultur}
                onChange={(e) => setKultur(e.target.value)}
                className="mobile-start-select"
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: { sx: { zIndex: 1500 } },
                }}
                fullWidth
              >
                <MenuItem value="">
                  <em>Bitte wählen</em>
                </MenuItem>
                {kulturOptions.map((k) => (
                  <MenuItem key={k.id} value={String(k.id)}>
                    {k.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="medium"
                label="Ertrag"
                value={ertrag}
                onChange={(e) => setErtrag(e.target.value)}
                className="mobile-start-select"
                InputLabelProps={{ shrink: true }}
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: { sx: { zIndex: 1500 } },
                }}
                fullWidth
              >
                <MenuItem value="">
                  <em>Bitte wählen</em>
                </MenuItem>
                {ertragOptions.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Mobile Start Screen - wird angezeigt bis alle Felder ausgewählt sind */}
      {isMobile && showStartScreen && (
        <Box className={`mobile-start-screen start-overlay ${allSelected ? 'fade-out' : ''}`}>
          <Typography variant="h5" className="mobile-start-title">
            Willkommen beim KaRisMa Klima-Risiko-Manager
          </Typography>
          <Typography variant="body1" className="mobile-start-subtitle">
            Bitte geben Sie die folgenden Daten ein bzw. treffen Sie eine Auswahl, um Ergebnisse angezeigt zu bekommen.
          </Typography>
          <StartDisclaimer />
          <Stack spacing={2} className="mobile-start-selects">
            {renderGebietseinheitAutocomplete({
              size: 'medium',
              className: 'mobile-start-select',
              fullWidth: true,
              popperZIndex: 1500,
            })}
            <TextField
              select
              size="medium"
              label="Kultur"
              value={kultur}
              onChange={(e) => setKultur(e.target.value)}
              className="mobile-start-select"
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                MenuProps: { sx: { zIndex: 1500 } },
              }}
              fullWidth
            >
              <MenuItem value="">
                <em>Bitte wählen</em>
              </MenuItem>
              {kulturOptions.map((k) => (
                <MenuItem key={k.id} value={String(k.id)}>
                  {k.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="medium"
              label="Ertrag"
              value={ertrag}
              onChange={(e) => setErtrag(e.target.value)}
              className="mobile-start-select"
              InputLabelProps={{ shrink: true }}
              SelectProps={{
                displayEmpty: true,
                MenuProps: { sx: { zIndex: 1500 } },
              }}
              fullWidth
            >
              <MenuItem value="">
                <em>Bitte wählen</em>
              </MenuItem>
              {ertragOptions.map((e) => (
                <MenuItem key={e.id} value={String(e.id)}>
                  {e.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>
      )}

      <Drawer
        anchor="left"
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box className="mobile-drawer" role="presentation">
          <Typography variant="subtitle1" className="drawer-title">
            Einstellungen
          </Typography>
          <Stack spacing={2} className="drawer-select-stack">
            {renderGebietseinheitAutocomplete({
              size: 'small',
              className: 'drawer-select',
              fullWidth: true,
            })}
            <TextField
              select
              size="small"
              label="Kultur"
              value={kultur}
              onChange={(e) => setKultur(e.target.value)}
              className="drawer-select"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              fullWidth
            >
              <MenuItem value="">
                <em>Bitte wählen</em>
              </MenuItem>
              {kulturOptions.map((k) => (
                <MenuItem key={k.id} value={String(k.id)}>
                  {k.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Ertrag"
              value={ertrag}
              onChange={(e) => setErtrag(e.target.value)}
              className="drawer-select"
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              fullWidth
            >
              <MenuItem value="">
                <em>Bitte wählen</em>
              </MenuItem>
              {ertragOptions.map((e) => (
                <MenuItem key={e.id} value={String(e.id)}>
                  {e.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>
      </Drawer>

      {allSelected && currentView === 'climate' && (
        <>
          {!isMobile && (
            <Typography variant="h5" className="headline-title">
              Ortsspezifische Klimarisiken
            </Typography>
          )}

          <Container maxWidth="lg" className="content">
            <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            className="controls-row"
          >
            <Box flex={1}>
              <Stack spacing={1.5} alignItems="flex-start">
                {!isMobile && (
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      color="success"
                      className="pill-button"
                      disableElevation
                      onClick={() => selectMetricByName('15-tägige Trockenperiode')}
                    >
                      Trockenstress
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      className="pill-button"
                      disableElevation
                      onClick={() => selectMetricByName('15-tägige Nassperiode')}
                    >
                      Staunässe
                    </Button>
                  </Stack>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} className="metric-month-row">
                  <Select
                    size="small"
                    value={metric}
                    onChange={handleMetricChange}
                    className="metric-select"
                  >
                    {variableOptions.map((option) => (
                      <MenuItem key={option.id} value={String(option.id)}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    size="small"
                    value={selectedMonth}
                    onChange={handleMonthChange}
                    className="month-select"
                  >
                    {monthOptions.map((option) => (
                      <MenuItem key={option.value} value={String(option.value)}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Stack>
            </Box>

            <Box className="scenario-buttons" flex={1}>
              <Stack
                direction={{ xs: 'row', md: 'column' }}
                spacing={{ xs: 1, md: 1.5 }}
                alignItems={{ xs: 'stretch', md: 'flex-end' }}
                justifyContent={{ xs: 'space-between', md: 'flex-start' }}
                className="scenario-stack"
                sx={{ flexWrap: 'nowrap' }}
              >
                {scenarioOptions.map((option) => (
                  <Tooltip key={option.key} title={option.tooltip} arrow>
                    <Button
                      variant={scenario === option.key ? 'contained' : 'outlined'}
                      className="scenario-button"
                      sx={getScenarioButtonStyles(option.key, scenario === option.key)}
                      onClick={() => setScenario(option.key)}
                    >
                      {option.label}
                    </Button>
                  </Tooltip>
                ))}
              </Stack>
            </Box>
            </Stack>

            <Paper elevation={3} className="chart-card">
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              {!loading && !error && data && data.years.length < 2 ? (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                  <Typography variant="body1">Keine Datensätze vorhanden</Typography>
                </Box>
              ) : (
                <ReactECharts
                  option={chartOptions}
                  style={{ height: 420 }}
                  loadingOption={{ text: 'Lade Daten...' }}
                  showLoading={loading}
                />
              )}
            </Paper>

            {/* Weiter-Button unter dem Diagramm */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mb: 4 }}>
              <Button
                variant="contained"
                color="success"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setCurrentView('massnahmen')}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
              >
                Weiter
              </Button>
            </Box>
          </Container>
        </>
      )}

      {/* ===== Maßnahmen-View ============================================= */}
      {allSelected && currentView === 'massnahmen' && (
        <>
          <Typography variant="h5" className="headline-title">
            Anpassungsmaßnahmen und Bewertung
          </Typography>

          <Container maxWidth="lg" className="content">
            {/* --- Maßnahmen-Karussell --------------------------------------- */}
            <Paper elevation={1} className="massnahmen-carousel-card">
              <Box className="carousel-label-wrapper">
                <Typography variant="subtitle2" className="carousel-label">
                  Maßnahmen
                </Typography>
              </Box>

              <Box className="carousel-wrapper">
                <IconButton
                  className="carousel-arrow"
                  disabled={carouselStart <= 0}
                  onClick={() => setCarouselStart((s) => Math.max(0, s - CAROUSEL_PAGE_SIZE))}
                >
                  <ChevronLeftIcon fontSize="large" />
                </IconButton>

                <Box className="carousel-items">
                  {massnahmenList
                    .slice(carouselStart, carouselStart + CAROUSEL_PAGE_SIZE)
                    .map((m) => (
                      <Tooltip key={m.id} title={m.mouseoverText ?? ''} arrow>
                        <Paper
                          elevation={selectedMassnahmen.includes(m.id) ? 4 : 1}
                          className={`carousel-card ${selectedMassnahmen.includes(m.id) ? 'selected' : ''}`}
                          onClick={() =>
                            setSelectedMassnahmen((prev) => {
                              if (prev.includes(m.id)) {
                                return prev.filter((id) => id !== m.id);
                              }
                              const next = [...prev, m.id];
                              return next.length > 2 ? next.slice(-2) : next;
                            })
                          }
                        >
                          {m.bild ? (
                            <img src={m.bild} alt={m.name} className="carousel-img" />
                          ) : (
                            <Box className="carousel-img-placeholder" />
                          )}
                          <Typography variant="body2" className="carousel-card-name">
                            {m.name}
                          </Typography>
                        </Paper>
                      </Tooltip>
                    ))}
                </Box>

                <IconButton
                  className="carousel-arrow"
                  disabled={carouselStart + CAROUSEL_PAGE_SIZE >= massnahmenList.length}
                  onClick={() => setCarouselStart((s) => s + CAROUSEL_PAGE_SIZE)}
                >
                  <ChevronRightIcon fontSize="large" />
                </IconButton>
              </Box>
            </Paper>

            {/* --- Tabs: Deckungsbeitrag / Ertrag + Szenario-Auswahl -------- */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              className="massnahmen-controls"
              sx={{ mt: 2, mb: 1, flexWrap: 'wrap', gap: 1 }}
            >
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  disableElevation
                  className="tab-button"
                  sx={{
                    backgroundColor: massnahmenTab === 'deckungsbeitrag' ? '#ff9800' : '#bdbdbd',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '20px',
                    '&:hover': { backgroundColor: massnahmenTab === 'deckungsbeitrag' ? '#fb8c00' : '#9e9e9e' },
                  }}
                  onClick={() => setMassnahmenTab('deckungsbeitrag')}
                >
                  Deckungsbeitrag
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  className="tab-button"
                  sx={{
                    backgroundColor: massnahmenTab === 'ertrag' ? '#4caf50' : '#bdbdbd',
                    color: '#fff',
                    textTransform: 'none',
                    borderRadius: '20px',
                    '&:hover': { backgroundColor: massnahmenTab === 'ertrag' ? '#43a047' : '#9e9e9e' },
                  }}
                  onClick={() => setMassnahmenTab('ertrag')}
                >
                  Ertrag
                </Button>
              </Stack>

              <Stack direction="row" spacing={1}>
                {scenarioOptions.map((opt) => (
                  <Button
                    key={opt.key}
                    variant={scenario === opt.key ? 'contained' : 'outlined'}
                    className="scenario-button"
                    sx={getScenarioButtonStyles(opt.key, scenario === opt.key)}
                    onClick={() => setScenario(opt.key)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </Stack>
            </Stack>

            {/* --- Hauptbereich: Eingabefelder + Diagramm ------------------- */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              {/* Linke Spalte: DB-Komponenten (Ø 2025-2028) */}
              <Paper elevation={1} className="kosten-panel">
                <Stack spacing={1.5}>
                  {DB_KOMPONENTEN_FIELDS.map((field) => (
                    <Box key={field.key}>
                      <Typography variant="body2" className="kosten-label">
                        {field.label} ({dbEinheiten[field.key] ?? field.defaultEinheit})
                      </Typography>
                      <TextField
                        size="small"
                        type="text"
                        inputMode="decimal"
                        className="kosten-input"
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                          }
                        }}
                        value={
                          dbInputFocusedKey === field.key
                            ? dbInputEditText
                            : formatGermanDecimal(dbInputs[field.key])
                        }
                        onFocus={() => {
                          setDbInputFocusedKey(field.key);
                          setDbInputEditText(formatGermanDecimal(dbInputs[field.key]));
                        }}
                        onBlur={() => {
                          setDbInputFocusedKey(null);
                          setDbInputEditText('');
                        }}
                        onChange={(e) => {
                          const raw = normalizePastedDecimalInput(e.target.value);
                          if (!isValidGermanDecimalInput(raw)) return;
                          setDbInputEditText(raw);
                          const num = parseGermanDecimal(raw);
                          if (num !== null) {
                            setDbInputs((prev) => ({
                              ...prev,
                              [field.key]: num,
                            }));
                          }
                        }}
                        fullWidth
                        disabled={dbKomponentenLoading}
                      />
                    </Box>
                  ))}
                </Stack>
              </Paper>

              {/* Rechte Spalte: Diagramm */}
              <Paper elevation={3} className="chart-card" sx={{ flex: 1 }}>
                <ReactECharts
                  option={oekonomieChartOptions}
                  notMerge
                  style={{ height: 400 }}
                  loadingOption={{ text: 'Lade Daten...' }}
                  showLoading={massnahmenTab === 'ertrag' ? oekonomieLoading : dbKomponentenLoading}
                />
              </Paper>
            </Stack>

            {/* --- Navigationsleiste unten ----------------------------------- */}
            <Box className="massnahmen-nav-bar">
              <Button
                variant="contained"
                color="success"
                startIcon={<ArrowBackIcon />}
                onClick={() => setCurrentView('climate')}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
              >
                Zurück
              </Button>

              <Box sx={{ flex: 1 }} />

              <Button
                variant="contained"
                color="success"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setCurrentView('oekonomische-auswirkungen')}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
              >
                Weiter
              </Button>
            </Box>
          </Container>
        </>
      )}

      {allSelected && currentView === 'oekonomische-auswirkungen' && (
        <>
          <Typography variant="h5" className="headline-title">
            Ökonomische Auswirkungen von empfohlenen Anpassungsmaßnahmen auf Landkreisebene
          </Typography>

          <Container maxWidth="lg" className="content">
            <Paper elevation={3} className="impact-filter-card">
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  size="small"
                  label="Kennzahl"
                  value={impactVariable}
                  onChange={(e) => setImpactVariable(e.target.value)}
                  className="impact-select"
                >
                  {impactVariableOptions.map((option) => (
                    <MenuItem key={option.id} value={String(option.id)}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Jahre"
                  value={impactYearRange}
                  onChange={(e) => setImpactYearRange(e.target.value as YearRangeOption)}
                  className="impact-select"
                >
                  <MenuItem value="aktuell">aktuell</MenuItem>
                  <MenuItem value="2030-2039">2030-2039</MenuItem>
                  <MenuItem value="2040-2049">2040-2049</MenuItem>
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Szenario"
                  value={String(impactScenarioId)}
                  onChange={(e) => setImpactScenarioId(Number(e.target.value) as ImpactScenarioId)}
                  className="impact-select"
                >
                  {impactScenarioOptions.map((option) => (
                    <MenuItem key={option.id} value={String(option.id)}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  size="small"
                  label="Massnahme"
                  value={impactMassnahme}
                  onChange={(e) => setImpactMassnahme(e.target.value)}
                  className="impact-select"
                >
                  {massnahmenDropdownList.map((option) => (
                    <MenuItem key={option.id} value={String(option.id)}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Button
                  variant={hasImpactComparison ? 'contained' : 'outlined'}
                  color={hasImpactComparison ? 'error' : 'success'}
                  onClick={handleToggleImpactComparison}
                  disabled={!hasImpactComparison && (landkreisLoading || !landkreisGeoJson || landkreisValues.length === 0)}
                  className="impact-compare-button"
                >
                  {hasImpactComparison ? 'Vergleich entfernen' : 'Vergleichen'}
                </Button>
              </Stack>
            </Paper>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ mt: 2 }}
              className={`impact-map-row ${hasImpactComparison ? '' : 'single-map'}`.trim()}
            >
              <Paper elevation={3} className="chart-card impact-map-card">
                <Typography variant="subtitle1" className="impact-map-title">
                  Aktuelle Karte
                </Typography>
                {landkreisError && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {landkreisError}
                  </Typography>
                )}
                <ReactECharts
                  option={landkreisChartOptions}
                  style={{ height: 520 }}
                  loadingOption={{ text: 'Lade Landkreisdaten...' }}
                  showLoading={landkreisLoading}
                  onEvents={landkreisChartEvents}
                />
              </Paper>

              {impactComparisonSnapshot && (
                <Paper elevation={3} className="chart-card impact-map-card">
                  <Typography variant="subtitle1" className="impact-map-title">
                    Vergleichskarte (fixiert)
                  </Typography>
                  <ReactECharts
                    option={comparisonLandkreisChartOptions}
                    style={{ height: 520 }}
                    loadingOption={{ text: 'Lade Landkreisdaten...' }}
                    showLoading={false}
                  />
                  <Box className="impact-comparison-meta">
                    <Typography variant="body2"><strong>Kultur:</strong> {impactComparisonSnapshot.filterLabels.kultur}</Typography>
                    <Typography variant="body2"><strong>Ertrag:</strong> {impactComparisonSnapshot.filterLabels.ertrag}</Typography>
                    <Typography variant="body2"><strong>Kennzahl:</strong> {impactComparisonSnapshot.filterLabels.kennzahl}</Typography>
                    <Typography variant="body2"><strong>Jahre:</strong> {impactComparisonSnapshot.filterLabels.jahre}</Typography>
                    <Typography variant="body2"><strong>Szenario:</strong> {impactComparisonSnapshot.filterLabels.szenario}</Typography>
                    <Typography variant="body2"><strong>Massnahme:</strong> {impactComparisonSnapshot.filterLabels.massnahme}</Typography>
                  </Box>
                </Paper>
              )}
            </Stack>

            <Box className="massnahmen-nav-bar">
              <Button
                variant="contained"
                color="success"
                startIcon={<ArrowBackIcon />}
                onClick={() => setCurrentView('massnahmen')}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
              >
                Zurück
              </Button>

              <Box sx={{ flex: 1 }} />

              <Button
                variant="contained"
                color="success"
                endIcon={<ArrowForwardIcon />}
                onClick={() => setCurrentView('zusaetzliche-effekte')}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
              >
                Weiter
              </Button>
            </Box>
          </Container>
        </>
      )}

      {allSelected && currentView === 'zusaetzliche-effekte' && (
        <>
          <Typography variant="h5" className="headline-title">
            Anpassungsmaßnahmen und Nebeneffekte
          </Typography>

          <Container maxWidth="lg" className="content">
            <Paper elevation={3} className="impact-filter-card">
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
              >
                {nebeneffektPrimaryCategory === 'massnahme' ? (
                  <TextField
                    select
                    size="small"
                    label="Maßnahmen"
                    value={selectedNebeneffektMassnahme}
                    onChange={(e) => {
                      const val = e.target.value;
                      hoveredOuterNodeRef.current = null;
                      setHoveredOuterNode(null);
                      setSelectedNebeneffektDropdown('');
                      setSelectedNebeneffektMassnahme(val);
                      setNebeneffektEntries([]);
                      if (val) setNebeneffektPrimaryCategory('massnahme');
                    }}
                    className="impact-select"
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">
                      <em>Keine Auswahl</em>
                    </MenuItem>
                    {nebeneffektMassnahmen.map((option) => (
                      <MenuItem key={`m-${option.id}`} value={String(option.id)}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    select
                    size="small"
                    label="Nebeneffekte"
                    value={selectedNebeneffektDropdown}
                    onChange={(e) => {
                      const val = e.target.value;
                      hoveredOuterNodeRef.current = null;
                      setHoveredOuterNode(null);
                      setSelectedNebeneffektMassnahme('');
                      setSelectedNebeneffektDropdown(val);
                      setNebeneffektEntries([]);
                      if (val) setNebeneffektPrimaryCategory('nebeneffekt');
                    }}
                    className="impact-select"
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">
                      <em>Keine Auswahl</em>
                    </MenuItem>
                    {nebeneffekteDropdownList.map((option) => (
                      <MenuItem key={`n-${option.id}`} value={String(option.id)}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
                <IconButton
                  onClick={() => {
                    hoveredOuterNodeRef.current = null;
                    setHoveredOuterNode(null);
                    setNebeneffektPrimaryCategory((prev) =>
                      prev === 'massnahme' ? 'nebeneffekt' : 'massnahme'
                    );
                  }}
                  aria-label="Maßnahmen und Nebeneffekte tauschen"
                  sx={{ flexShrink: 0 }}
                >
                  <SwapHorizIcon />
                </IconButton>
                {nebeneffektPrimaryCategory === 'massnahme' ? (
                  <TextField
                    select
                    size="small"
                    label="Nebeneffekte"
                    value={selectedNebeneffektDropdown}
                    onChange={(e) => {
                      const val = e.target.value;
                      hoveredOuterNodeRef.current = null;
                      setHoveredOuterNode(null);
                      setSelectedNebeneffektMassnahme('');
                      setSelectedNebeneffektDropdown(val);
                      setNebeneffektEntries([]);
                      if (val) setNebeneffektPrimaryCategory('nebeneffekt');
                    }}
                    className="impact-select"
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">
                      <em>Keine Auswahl</em>
                    </MenuItem>
                    {nebeneffekteDropdownList.map((option) => (
                      <MenuItem key={`n-${option.id}`} value={String(option.id)}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <TextField
                    select
                    size="small"
                    label="Maßnahmen"
                    value={selectedNebeneffektMassnahme}
                    onChange={(e) => {
                      const val = e.target.value;
                      hoveredOuterNodeRef.current = null;
                      setHoveredOuterNode(null);
                      setSelectedNebeneffektDropdown('');
                      setSelectedNebeneffektMassnahme(val);
                      setNebeneffektEntries([]);
                      if (val) setNebeneffektPrimaryCategory('massnahme');
                    }}
                    className="impact-select"
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value="">
                      <em>Keine Auswahl</em>
                    </MenuItem>
                    {nebeneffektMassnahmen.map((option) => (
                      <MenuItem key={`m-${option.id}`} value={String(option.id)}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
                Positiv
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#c62828' }} />
                Negativ
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#757575' }} />
                Neutral
              </Typography>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
              <Paper elevation={3} className="chart-card" sx={{ flex: 1, minWidth: 0 }}>
                {nebeneffektError && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {nebeneffektError}
                  </Typography>
                )}

                {!nebeneffektError && !nebeneffektLoading && nebeneffektEntries.length === 0 && (
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <Typography variant="body1">
                      {!selectedNebeneffektMassnahme && !selectedNebeneffektDropdown
                        ? 'Bitte wählen Sie eine Maßnahme oder einen Nebeneffekt aus.'
                        : activeNebeneffektChartMode === 'nebeneffekt'
                          ? 'Für den gewählten Nebeneffekt wurden keine Maßnahmen gefunden.'
                          : 'Für die gewählte Maßnahme wurden keine Nebeneffekte gefunden.'}
                    </Typography>
                  </Box>
                )}

                {nebeneffektEntries.length > 0 && (
                  <ReactECharts
                    key={`${nebeneffektDataMode}-${selectedNebeneffektMassnahme}-${selectedNebeneffektDropdown}`}
                    option={nebeneffektChartOptions}
                    notMerge={true}
                    style={{ height: 520 }}
                    onEvents={nebeneffektChartEvents}
                  />
                )}
                {nebeneffektLoading && nebeneffektEntries.length === 0 && (
                  <Box sx={{ py: 8, textAlign: 'center' }}>
                    <Typography variant="body1">Lade Nebeneffekte...</Typography>
                  </Box>
                )}
              </Paper>

              <Paper
                elevation={3}
                sx={{
                  width: { xs: '100%', md: 320 },
                  minHeight: 200,
                  p: 2.5,
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'opacity 0.2s ease',
                  opacity: (hoveredOuterNode ?? hoveredOuterNodeRef.current) ? 1 : 0.4,
                }}
              >
                {(() => {
                  const displayNode = hoveredOuterNode ?? hoveredOuterNodeRef.current;
                  if (!displayNode) {
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                          Fahren Sie mit der Maus über einen Punkt, um Details zur Verknüpfung anzuzeigen.
                        </Typography>
                      </Box>
                    );
                  }
                  return (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                        {displayNode.name}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Bewertung:</strong>{' '}
                        <Box
                          component="span"
                          sx={{
                            color: displayNode.bewertung === 1
                              ? '#2e7d32'
                              : displayNode.bewertung === 2
                                ? '#c62828'
                                : '#757575',
                            fontWeight: 600,
                          }}
                        >
                          {displayNode.bewertung === 1
                            ? 'Positiv'
                            : displayNode.bewertung === 2
                              ? 'Negativ'
                              : 'Neutral'}
                        </Box>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>
                        {displayNode.info || 'Keine Zusatzinformation hinterlegt.'}
                      </Typography>
                      {displayNode.link && (
                        <Typography variant="body2">
                          <a
                            href={displayNode.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1565c0' }}
                          >
                            Weiterführender Link
                          </a>
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Paper>
            </Stack>

            <Box className="massnahmen-nav-bar">
              <Button
                variant="contained"
                color="success"
                startIcon={<ArrowBackIcon />}
                onClick={() => setCurrentView('oekonomische-auswirkungen')}
                sx={{ borderRadius: '8px', textTransform: 'none', px: 3, py: 1 }}
              >
                Zurück
              </Button>
            </Box>
          </Container>
        </>
      )}

      <Dialog
        open={landkreisModalOpen}
        onClose={() => setLandkreisModalOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>
          Landkreis: {landkreisModalData?.landkreis.name ?? selectedLandkreisName}
        </DialogTitle>
        <DialogContent dividers>
          {landkreisModalError && (
            <Typography color="error">{landkreisModalError}</Typography>
          )}
          {!landkreisModalError && landkreisModalLoading && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body1">Lade Diagrammdaten...</Typography>
            </Box>
          )}
          {!landkreisModalError && !landkreisModalLoading && hasLandkreisModalSeries && (
            <ReactECharts
              option={landkreisModalChartOptions}
              style={{ height: isMobile ? 320 : 420 }}
              showLoading={false}
            />
          )}
          {!landkreisModalError && !landkreisModalLoading && !hasLandkreisModalSeries && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body1">Für diesen Landkreis sind keine Zeitreihen verfügbar.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLandkreisModalOpen(false)} variant="contained" color="success">
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {allSelected && (
        <Box
          ref={chatRef}
          className={chatClassNames}
          style={
            isMobile
              ? { left: 0, right: 0, bottom: 0, top: 'auto', transform: 'none' }
              : { top: chatPos.top, left: chatPos.left, bottom: 'auto', transform: 'none' }
          }
        >
          {chatCollapsed ? (
            <Box
              className="chat-collapsed-bar"
              onMouseDown={!isMobile ? startDrag : undefined}
              onClick={() => setChatCollapsed(false)}
            >
              <Typography variant="body2" color="text.secondary">
                Nachricht eingeben
              </Typography>
            </Box>
          ) : (
            <>
              <Box className="chat-header" onMouseDown={!isMobile ? startDrag : undefined}>
                <Typography variant="subtitle2">Chatbot</Typography>
                <Button size="small" onClick={() => setChatCollapsed(true)}>
                  Ausblenden
                </Button>
              </Box>
              <Box className="chat-body">
                {chatMessages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Stelle eine Frage zu den dargestellten Risiken.
                  </Typography>
                ) : (
                  chatMessages.map((msg, idx) => (
                    <Box key={idx} className={`chat-message ${msg.sender}`}>
                      <Typography variant="body2">{msg.text}</Typography>
                    </Box>
                  ))
                )}
              </Box>
              <Box className="chat-input-row">
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Nachricht eingeben..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <Button variant="contained" color="success" onClick={handleSendMessage}>
                  Senden
                </Button>
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default App;
